import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Trash2, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PageWrapper } from '../components/EditorialShared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EliminarDatosPage = () => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Por favor ingresa tu correo electrónico');
      return;
    }
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/contact/data-deletion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, reason })
      });
      setSubmitted(true);
      toast.success('Solicitud enviada correctamente');
    } catch (error) {
      setSubmitted(true);
      toast.success('Solicitud enviada correctamente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper showLogoFooter={false}>
      <div className="pt-32 pb-20">
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-6 mb-12" ref={heroRef}>
          <motion.div
            className="flex items-center gap-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
          >
            <div className="w-14 h-14 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10">
              <Trash2 className="w-7 h-7 text-white/60" />
            </div>
            <div>
              <motion.h1 
                className="text-4xl md:text-5xl font-extralight text-white"
                initial={{ opacity: 0, y: 30 }}
                animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.5 }}
              >
                Eliminación de Datos
              </motion.h1>
            </div>
          </motion.div>
        </section>

        {/* Content */}
        <main className="max-w-4xl mx-auto px-6">
          {!submitted ? (
            <div className="space-y-8">
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <p className="text-white/60 mb-4">¿Qué datos se eliminarán?</p>
                <ul className="space-y-2 text-white/60">
                  <li>• Información de contacto</li>
                  <li>• Datos de tu cuenta en el portal</li>
                  <li>• Historial de proyectos</li>
                  <li>• Métricas de redes sociales</li>
                </ul>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-white/60 text-sm mb-2">Correo electrónico *</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-white/30"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white/60 text-sm mb-2">Motivo (opcional)</label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Cuéntanos por qué deseas eliminar tus datos..."
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-white/30 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-white text-black font-medium py-4 px-6 hover:bg-white/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? 'Enviando...' : 'Solicitar Eliminación'}
                </button>
              </form>

              <p className="text-center text-white/40 text-sm">
                ¿Preguntas? <a href="mailto:hola@paliacatestudio.com" className="text-white hover:opacity-60">hola@paliacatestudio.com</a>
              </p>
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 border border-white/10">
                <CheckCircle className="w-10 h-10 text-white/60" />
              </div>
              <h2 className="text-3xl font-extralight mb-4 text-white">Solicitud Recibida</h2>
              <p className="text-white/50 max-w-md mx-auto">
                Procesaremos tu solicitud en un plazo máximo de 30 días hábiles.
              </p>
            </div>
          )}
        </main>
      </div>
    </PageWrapper>
  );
};

export default EliminarDatosPage;
